
import React, { useEffect, useState, useRef } from "react";
import { createDish } from "../api/api_dishes";
import { fetchSubcategoriesByAffiliateCategory } from "../api/api_categories";
import Select from "react-select";
import { useTranslation } from '../hooks/useTranslation';

const TableRowItemNew = ({ categoryId, adding, setAdding, onDishCreated }) => {
  const { t } = useTranslation();
  const [newItemName, setNewItemName] = useState("");
  const [newItemImage, setNewItemImage] = useState("");
  const [inputErrorMessage, setInputErrorMessage] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const inputErrorRef = useRef(null);
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  useEffect(() => {
    if (!adding) return;
    const loadSubs = async () => {
      try {
        const res = await fetchSubcategoriesByAffiliateCategory(categoryId);
        const items = res?.data?.data || [];
        setSubcategories(items);
      } catch (e) {
        console.error("Failed to load subcategories", e);
        setSubcategories([]);
      }
    };
    loadSubs();
  }, [adding, categoryId]);

  // Match react-select styling to our input-field look & feel
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      backgroundColor: '#ffffff',
      borderColor: state.isFocused ? '#9cd7b6' : '#D1D5DB', // gray-300
      boxShadow: state.isFocused ? '0 0 0 3px rgba(156,215,182,0.35)' : 'none',
      '&:hover': { borderColor: '#9cd7b6' },
      borderRadius: '0.5rem', // rounded-lg
      minHeight: 44,
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0.375rem 0.75rem', // py-1.5 px-3
    }),
    input: (base) => ({
      ...base,
      margin: 0,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      color: '#6B7280', // gray-500
    }),
    placeholder: (base) => ({
      ...base,
      color: '#9CA3AF', // gray-400
    }),
    singleValue: (base) => ({
      ...base,
      color: '#111827', // gray-900
    }),
    menu: (base) => ({
      ...base,
      zIndex: 50,
      borderRadius: '0.5rem',
      overflow: 'hidden',
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isFocused ? 'rgba(156,215,182,0.15)' : '#ffffff',
      color: '#111827',
      '&:active': { backgroundColor: 'rgba(156,215,182,0.25)' },
    }),
  };

  const handleAddDish = async () => {
    if (!newItemName.trim()) {
      setInputErrorMessage("Välj namn för rätten.");
      return;
    }
    if (!selectedSubcategory?.value) {
      setInputErrorMessage("Välj en underkategori.");
      return;
    }

    setIsCreating(true);
    setInputErrorMessage("");

    try {
      const dishData = {
        name: newItemName.trim(),
        image: newItemImage.trim() || null,
        affiliate_category_id: categoryId,
        subcategory_id: Number(selectedSubcategory.value),
      };

      console.log("Creating dish with data:", dishData);
      const response = await createDish(dishData);
      console.log("Dish creation response:", response);

      // Handle different possible response structures from backend
      let createdDish = null;
      if (response.data) {
        // Try different possible response structures
        if (response.data.dish) {
          createdDish = response.data.dish;
        } else if (response.data.data) {
          createdDish = response.data.data;
        } else if (response.data.id) {
          // If response.data itself is the dish object
          createdDish = response.data;
        }
      }

      if (createdDish) {
        // Reset form
        setNewItemName("");
        setNewItemImage("");
        setInputErrorMessage("");
        setSelectedSubcategory(null);
        setAdding(false);
        
        // Notify parent component to refresh the dishes list
        if (onDishCreated) {
          onDishCreated(createdDish);
        }
      } else {
        console.error("Unexpected response structure:", response);
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Failed to create dish:", error);
      if (error.response?.data?.message) {
        setInputErrorMessage(error.response.data.message);
      } else if (error.response?.data?.errors) {
        const errorMessage = Object.values(error.response.data.errors).flat().join(', ');
        setInputErrorMessage(errorMessage);
      } else {
        setInputErrorMessage("Kunde inte skapa rätten. Försök igen.");
      }
    } finally {
      setIsCreating(false);
    }
  };

  if (!adding) {
    return null;
  }

  return (
    <div className="card shadow-none mb-6 mobile:mb-6 tablet:mb-8">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50 mobile:hidden">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('Namn')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('Underkategori')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('Bildens URL')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('Skapa')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* Desktop/Tablet layout */}
            <tr className="hover:bg-gray-50 mobile:hidden">
              {/* Namn */}
              <td className="px-6 py-5 whitespace-nowrap min-w-[12rem] tablet:min-w-[14rem] laptop:min-w-[18rem] desktop:min-w-[20rem]">
                <input
                  className="input-field"
                  type="text"
                  value={newItemName}
                  onChange={(e) => {
                    setInputErrorMessage("");
                    setNewItemName(e.target.value);
                  }}
                  placeholder={t('Namn')}
                  disabled={isCreating}
                />
              </td>
              {/* Underkategori (narrower on large screens) */}
              <td className="px-6 py-5 whitespace-nowrap mobile:w-full tablet:min-w-[14rem] laptop:min-w-[18rem] desktop:min-w-[22rem]">
                <Select
                  classNamePrefix="select"
                  className="w-full"
                  placeholder={t('Välj underkategori')}
                  isClearable
                  isSearchable
                  value={selectedSubcategory}
                  onChange={(opt) => {
                    setInputErrorMessage("");
                    setSelectedSubcategory(opt);
                  }}
                  options={subcategories.map(sc => ({ value: sc.id, label: sc.name }))}
                  isDisabled={isCreating}
                  styles={selectStyles}
                  menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                />
              </td>
              {/* Bildens URL (gets more space on desktop) */}
              <td className="px-6 py-5 whitespace-nowrap w-full">
                <input
                  className="input-field"
                  type="text"
                  value={newItemImage}
                  onChange={(e) => {
                    setInputErrorMessage("");
                    setNewItemImage(e.target.value);
                  }}
                  placeholder={t('Bild URL (valfritt)')}
                  disabled={isCreating}
                />
              </td>
              {/* Skapa (empty cell; actions moved to full-width row below) */}
              <td className="px-6 py-5 whitespace-nowrap"></td>
            </tr>
            {/* Action buttons row - full width block, responsive (hidden on mobile) */}
            <tr className="mobile:hidden">
              <td className="px-6 py-4 whitespace-nowrap" colSpan="4">
                <div className="grid grid-cols-1 gap-2 mobile:grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-2 desktop:grid-cols-2 max-w-xl">
                  <button
                    className="btn-success inline-flex items-center justify-center gap-2 disabled:opacity-50 w-full"
                    onClick={handleAddDish}
                    disabled={isCreating}
                  >
                    {isCreating ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <img 
                        src="/assets/icons/Check.png" 
                        alt="Create" 
                        className="w-4 h-4"
                      />
                    )}
                    {isCreating ? t('Sparar...') : t('Skapa ny rätt')}
                  </button>
                  <button
                    className="btn-secondary w-full"
                    onClick={() => setAdding(false)}
                    disabled={isCreating}
                  >
                    {t('Avbryt')}
                  </button>
                </div>
              </td>
            </tr>

            {/* Mobile layout: stacked fields */}
            <tr className="hidden mobile:table-row">
              <td className="px-4 py-4" colSpan="4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Namn</label>
                    <input
                      className="input-field w-full"
                      type="text"
                      value={newItemName}
                      onChange={(e) => {
                        setInputErrorMessage("");
                        setNewItemName(e.target.value);
                      }}
                      placeholder="Namn"
                      disabled={isCreating}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Underkategori</label>
                    <Select
                      classNamePrefix="select"
                      className="w-full"
                      placeholder="Välj underkategori"
                      isClearable
                      isSearchable
                      value={selectedSubcategory}
                      onChange={(opt) => {
                        setInputErrorMessage("");
                        setSelectedSubcategory(opt);
                      }}
                      options={subcategories.map(sc => ({ value: sc.id, label: sc.name }))}
                      isDisabled={isCreating}
                      styles={selectStyles}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Bildens URL</label>
                    <input
                      className="input-field w-full"
                      type="text"
                      value={newItemImage}
                      onChange={(e) => {
                        setInputErrorMessage("");
                        setNewItemImage(e.target.value);
                      }}
                      placeholder="Bildens URL (Valfri)"
                      disabled={isCreating}
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <button
                      className="btn-success inline-flex items-center justify-center gap-2 disabled:opacity-50 w-full"
                      onClick={handleAddDish}
                      disabled={isCreating}
                    >
                      {isCreating ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      ) : (
                                              <img 
                        src="/assets/icons/Check.png" 
                        alt="Create" 
                        className="w-4 h-4"
                      />
                      )}
                      {isCreating ? "Skapar..." : t('Skapa ny rätt')}
                    </button>
                    <button
                      className="btn-secondary w-full"
                      onClick={() => setAdding(false)}
                      disabled={isCreating}
                    >
                      {t('Avbryt')}
                    </button>
                  </div>
                  {inputErrorMessage && (
                    <div className="text-red-600 text-sm font-medium" ref={inputErrorRef}>
                      {inputErrorMessage}
                    </div>
                  )}
                </div>
              </td>
            </tr>
            {inputErrorMessage && (
              <tr>
                <td colSpan="3" className="px-6 py-2">
                  <div className="text-red-600 text-sm font-medium" ref={inputErrorRef}>
                    {inputErrorMessage}
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TableRowItemNew;
